/**
 * Route table shared by the gateway controllers and the mobile API client.
 * Paths are relative to {@link API_PREFIX}; functions build parameterised paths.
 */
const enc = (value: string) => encodeURIComponent(value);

export const routes = {
  admin: {
    base: '/admin',
    login: '/admin/auth/login',
    me: '/admin/auth/me',
    changePassword: '/admin/auth/password',
    overview: '/admin/overview',
    system: '/admin/system',
    users: '/admin/users',
    user: (id: string) => `/admin/users/${enc(id)}`,
    userSessions: (id: string) => `/admin/users/${enc(id)}/sessions`,
    /** POST: turns a method on for the account; DELETE: turns every method off (a lost phone). Both audited. */
    userTwoFactor: (id: string) => `/admin/users/${enc(id)}/two-factor`,
    /** DELETE: turns one method off for the account (`sms`, `totp`: a lost phone or app). Audited. */
    userTwoFactorMethod: (id: string, method: string) => `/admin/users/${enc(id)}/two-factor/${enc(method)}`,
    /** POST: lifts the pause after too many wrong authenticator codes. Audited. */
    userTwoFactorUnlock: (id: string) => `/admin/users/${enc(id)}/two-factor/unlock`,
    /** GET: the account's security history (`security-events.ts`), newest first. */
    userSecurityEvents: (id: string) => `/admin/users/${enc(id)}/security-events`,
    /** GET (admin role): the numbers in the account's mail-server entry; PUT: sets or removes the one codes go to. */
    userPhone: (id: string) => `/admin/users/${enc(id)}/phone`,
    session: (id: string) => `/admin/sessions/${enc(id)}`,
    devices: '/admin/devices',
    device: (id: string) => `/admin/devices/${enc(id)}`,
    deviceTest: (id: string) => `/admin/devices/${enc(id)}/test`,
    installations: '/admin/installations',
    installation: (id: string) => `/admin/installations/${enc(id)}`,
    installationUsage: (id: string) => `/admin/installations/${enc(id)}/usage`,
    userUsage: (id: string) => `/admin/users/${enc(id)}/usage`,
    usageTop: '/admin/usage/top',
    pushSend: '/admin/push/send',
    deliveries: '/admin/deliveries',
    tenants: '/admin/tenants',
    tenant: (id: string) => `/admin/tenants/${enc(id)}`,
    tenantTest: (id: string) => `/admin/tenants/${enc(id)}/test`,
    /** POST: read the mail server's domain list now (answers `TenantServerDomains`). */
    tenantDomainSync: (id: string) => `/admin/tenants/${enc(id)}/domains/sync`,
    audit: '/admin/audit',
    admins: '/admin/admins',
    adminUser: (id: string) => `/admin/admins/${enc(id)}`,
    brands: '/admin/brands',
    brand: (id: string) => `/admin/brands/${enc(id)}`,
    /** One uploaded logo; `key` is a `BrandUploadKey`. PUT replaces it, DELETE returns to the default artwork. */
    brandArtwork: (id: string, key: string) => `/admin/brands/${enc(id)}/artwork/${enc(key)}`,
    suggestions: '/admin/suggestions',
    suggestion: (id: string) => `/admin/suggestions/${enc(id)}`,
    suggestionReply: (id: string) => `/admin/suggestions/${enc(id)}/reply`,
    suggestionStatus: (id: string) => `/admin/suggestions/${enc(id)}/status`,
    /** GET/PUT: the SMS provider and its account (`sms-admin.ts`); the password is never returned. */
    sms: '/admin/sms',
    /** POST: asks the provider about the account (credit, sender names); sends nothing. */
    smsCheck: '/admin/sms/check',
    /** POST: sends one test message. */
    smsTest: '/admin/sms/test',
    /** GET/PUT: two-step sign-in for the whole server and each method's switch (the valve for an outage). */
    twoFactor: '/admin/two-factor',
    /** GET: every account's security history, newest first; filters by type, method, actor and address. */
    securityEvents: '/admin/security-events',
  },
  auth: {
    base: '/auth',
    login: '/auth/login',
    /** Public: change a password the server requires changed, then sign in (answers like login). */
    passwordExpired: '/auth/password/expired',
    refresh: '/auth/refresh',
    logout: '/auth/logout',
    me: '/auth/me',
    sessions: '/auth/sessions',
    session: (id: string) => `/auth/sessions/${enc(id)}`,
    /** Second step of a sign-in that answered `twoFactor` (see `two-factor.ts`). */
    twoFactor: {
      /** Public: finishes an app's sign-in with a code (answers like login). */
      verify: '/auth/two-factor/verify',
      /** Public: another code by SMS for the same sign-in (apps and browsers). */
      send: '/auth/two-factor/send',
    },
    /**
     * Browser sign-in (ADR 0007): the refresh token lives only in an HttpOnly cookie scoped
     * to this prefix; every call needs an allowed `Origin` and `X-Requested-With: fetch`.
     */
    web: {
      base: '/auth/web',
      login: '/auth/web/login',
      refresh: '/auth/web/refresh',
      logout: '/auth/web/logout',
      /** Finishes a browser sign-in with a code; sets the refresh cookie like login. */
      twoFactorVerify: '/auth/web/two-factor/verify',
    },
  },
  devices: {
    base: '/devices',
    collection: '/devices',
    item: (id: string) => `/devices/${enc(id)}`,
    test: (id: string) => `/devices/${enc(id)}/test`,
  },
  mail: {
    base: '/mail',
    folders: '/mail/folders',
    folder: (id: string) => `/mail/folders/${enc(id)}`,
    folderAction: (id: string) => `/mail/folders/${enc(id)}/actions`,
    conversations: '/mail/conversations',
    conversation: (id: string) => `/mail/conversations/${enc(id)}`,
    conversationActions: '/mail/conversations/actions',
    messages: '/mail/messages',
    message: (id: string) => `/mail/messages/${enc(id)}`,
    messageActions: '/mail/messages/actions',
    attachment: (messageId: string, part: string) =>
      `/mail/messages/${enc(messageId)}/attachments/${enc(part)}`,
    /** Every file of one message in a single archive (GAP B15). */
    attachmentArchive: (messageId: string) => `/mail/messages/${enc(messageId)}/attachments.zip`,
    /** The message as it was received (RFC 822), for "show original" and for saving it. */
    messageRaw: (messageId: string) => `/mail/messages/${enc(messageId)}/raw`,
    send: '/mail/send',
    /** Delayed sends the gateway still holds for the account (server-side undo send). */
    sends: '/mail/sends',
    /** DELETE cancels a delayed send inside its window and leaves the draft in place. */
    pendingSend: (id: string) => `/mail/sends/${enc(id)}`,
    drafts: '/mail/drafts',
    draft: (id: string) => `/mail/drafts/${enc(id)}`,
    uploads: '/mail/uploads',
    /** Relays an image referenced by a message body (see RemoteImageQuerySchema). */
    remoteImage: '/mail/remote-image',
    search: '/mail/search',
    autocomplete: '/mail/autocomplete',
    tags: '/mail/tags',
    tag: (id: string) => `/mail/tags/${enc(id)}`,
    signatures: '/mail/signatures',
    signature: (id: string) => `/mail/signatures/${enc(id)}`,
    sync: '/mail/sync',
    blockedSenders: '/mail/blocked-senders',
    blockedSender: (email: string) => `/mail/blocked-senders/${enc(email)}`,
    knownSenders: '/mail/senders/known',
    /** Mail rules in the order they run (see `mail/rules.ts`); POST appends one. */
    rules: '/mail/rules',
    /** One rule; `id` is the rule's name, which is the only identity the mail server has for it. */
    rule: (id: string) => `/mail/rules/${enc(id)}`,
    /** The whole order at once — rules run in sequence, so moving one moves the rest. */
    rulesReorder: '/mail/rules/reorder',
  },
  calendar: {
    base: '/calendar',
    calendars: '/calendar/calendars',
    events: '/calendar/events',
    event: (id: string) => `/calendar/events/${enc(id)}`,
    inviteReply: '/calendar/invites/reply',
    freeBusy: '/calendar/free-busy',
  },
  contacts: {
    base: '/contacts',
    folders: '/contacts/folders',
    collection: '/contacts',
    item: (id: string) => `/contacts/${enc(id)}`,
    actions: '/contacts/actions',
    photo: (id: string) => `/contacts/${enc(id)}/photo`,
  },
  tasks: {
    base: '/tasks',
    collection: '/tasks',
    item: (id: string) => `/tasks/${enc(id)}`,
  },
  settings: {
    base: '/settings',
    account: '/settings/account',
    prefs: '/settings/prefs',
    outOfOffice: '/settings/out-of-office',
    /** The account's own password; the mail server checks the current one and its policy. */
    password: '/settings/password',
    /** Where a copy of arriving mail is sent, and whether one stays here. */
    forwarding: '/settings/forwarding',
    /** The addresses this mailbox may write from; POST adds one. */
    identities: '/settings/identities',
    identity: (id: string) => `/settings/identities/${enc(id)}`,
    /** The account's own request to close the mailbox and delete what it holds. */
    accountDeletion: '/settings/account-deletion',
    /** Two-step sign-in: its state, the phone number it sends codes to, the authenticator app, turning it off. */
    twoFactor: {
      status: '/settings/two-factor',
      /** Sends a code to the number to prove (password again). */
      phoneSetup: '/settings/two-factor/sms/setup',
      phoneSetupResend: '/settings/two-factor/sms/setup/resend',
      /** The code: stores the number with the mail server and, if asked, turns the SMS step on. */
      phoneSetupConfirm: '/settings/two-factor/sms/setup/confirm',
      /** A new key for an authenticator app (password again); shown once, until the app's first code confirms it. */
      totpSetup: '/settings/two-factor/totp/setup',
      /** The app's first code: the authenticator method turns on, or its new key replaces the old one. */
      totpConfirm: '/settings/two-factor/totp/confirm',
      disable: '/settings/two-factor/disable',
    },
  },
  suggestions: {
    base: '/suggestions',
    collection: '/suggestions',
    item: (id: string) => `/suggestions/${enc(id)}`,
    messages: (id: string) => `/suggestions/${enc(id)}/messages`,
    read: (id: string) => `/suggestions/${enc(id)}/read`,
  },
  /** Real-time event stream (Server-Sent Events, Bearer): see `realtime.ts`. */
  events: '/events',
  content: {
    base: '/content',
    /** Signed, short-lived URLs of content routes, for browsers (see SignContentRequestSchema). */
    sign: '/content/sign',
  },
  branding: {
    base: '/branding',
    /** Public: the brand clients show (name, tagline, colours, artwork). */
    current: '/branding',
    /** Public, content-addressed artwork: `hash` is the first 16 hex digits of the file's SHA-256. */
    asset: (hash: string, file: string) => `/branding/assets/${enc(hash)}/${enc(file)}`,
  },
  dev: {
    base: '/dev',
    mockIncomingMail: '/dev/mock/incoming-mail',
    mockReset: '/dev/mock/reset',
    /** The text messages the console SMS provider "sent" (mock mode only): where tests read their codes. */
    mockSms: '/dev/mock/sms',
  },
  telemetry: {
    base: '/telemetry',
    /** Anonymous real-user measurements; no session, sampled by the client. */
    vitals: '/telemetry/vitals',
  },
  system: {
    health: '/health',
    ready: '/ready',
    metrics: '/metrics',
    docs: '/docs',
  },
} as const;

export type Routes = typeof routes;
